/*
 * QR Menu V2 - Premium Mobile-App Experience
 * Real API integration preserved, full new design from qrmenunewgen + screenshots
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { base_url, img_url } from '../api/index';
import {
  addItem,
  removeItem,
  increaseQuantity,
  decreaseQuantity,
  clearBasket,
} from '../redux/basketSlice';

import QRMenuWelcome from '../components/qrmenuV2/QRMenuWelcome';
import QRMenuPage, {
  QrCartItem,
  QrApprovedOrder,
  QrTrackingOrder,
} from '../components/qrmenuV2/QRMenuPage';
import { QrProduct } from '../components/qrmenuV2/ProductCard';
import { QrCategory } from '../components/qrmenuV2/CategoryNav';
import { QrStory } from '../components/qrmenuV2/MenuStories';
// QrCartNotification eski tip; cart bildirimleri artık DynamicIsland'a gidiyor.
import { QrUserProfile } from '../components/qrmenuV2/ProfileModal';
import { FeedbackPayload } from '../components/qrmenuV2/FeedbackModal';
import { QrLanguageProvider, getQrTranslations } from '../components/qrmenuV2/LanguageContext';
import QrcodeDontActive from '../components/QrcodeDontActive';
import QrcodeDontOrder from '../components/QrcodeDontOrder';
import {
  DynamicIslandProvider,
  useDynamicIsland,
} from '../components/qrmenuV2/DynamicIsland';
import { CustomerAuthProvider } from '../context/CustomerAuthContext';
import { CustomerFavoritesProvider } from '../context/CustomerFavoritesContext';
import {
  getQrAdisyon,
  identifyQrCustomer,
  getQrCustomerHistory,
  loadStoredQrPhone,
  saveQrPhone,
  clearQrPhone,
  QrAdisyonResponse,
  QrAdisyonCustomer,
  QrCustomerLifetime,
  QrHistoryResponse,
} from '../api/qrAdisyon';
import { useQrAdisyonRealtime } from '../hook/useQrAdisyonRealtime';
import {
  createQrWaiterCall,
  getQrActiveWaiterCall,
  cancelQrWaiterCall,
  QrWaiterActiveCall,
  QrWaiterReasonCode,
} from '../api/qrWaiterCall';
import { useQrWaiterCallRealtime } from '../hook/useQrWaiterCallRealtime';

const USER_STORAGE_KEY = 'rezmo-user-profile';

const loadStoredUser = (): QrUserProfile | null => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as QrUserProfile) : null;
  } catch {
    return null;
  }
};

// ---------- Tracking: localStorage helpers ----------
// Per the QR_ORDER_TRACKING.md spec, order IDs given by this table are
// persisted under `qr_order_ids:{uniqueUrl}` so the customer can recover
// their orders after a page reload (and to detect canceled/completed
// orders that no longer appear in the /orders/active feed).
const trackedOrdersKey = (uniqueUrl: string) => `qr_order_ids:${uniqueUrl}`;

const getRememberedOrderIds = (uniqueUrl: string): number[] => {
  try {
    const raw = localStorage.getItem(trackedOrdersKey(uniqueUrl));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  } catch {
    return [];
  }
};

const rememberOrderId = (uniqueUrl: string, orderId: number | string): void => {
  const id = Number(orderId);
  if (!Number.isFinite(id) || id <= 0) return;
  try {
    const ids = getRememberedOrderIds(uniqueUrl);
    if (!ids.includes(id)) {
      ids.push(id);
      // Cap to last 25 orders to keep storage tiny
      const trimmed = ids.slice(-25);
      localStorage.setItem(trackedOrdersKey(uniqueUrl), JSON.stringify(trimmed));
    }
  } catch {
    /* ignore quota / private mode errors */
  }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  };
};

// Apple Pay (web): check availability and trigger session
const isApplePayAvailable = (): boolean => {
  try {
    // @ts-ignore - ApplePaySession is global on Safari
    return typeof window !== 'undefined' && !!window.ApplePaySession && window.ApplePaySession.canMakePayments();
  } catch (e) {
    return false;
  }
};

/**
 * Body component — tüm provider'ların altında render olur, böylece
 * useDynamicIsland / useCustomerAuth hook'ları içeride kullanılabilir.
 * Her toast.X çağrısı island.show ile değiştirildi (react-toastify QR menüde artık yok).
 */
const RestaurantMenuV2Body: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { token } = useParams();
  const island = useDynamicIsland();

  // Restaurant & Menu State
  const [restaurant, setRestaurant] = useState<{
    name: string;
    logo: string;
    masaName: string;
    image?: string;
  }>({ name: '', logo: '', masaName: '' });
  const [menu, setMenu] = useState<any[]>([]);
  const [stockSets, setStockSets] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | number>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Orders State
  const [approvedOrders, setApprovedOrders] = useState<QrApprovedOrder[]>([]);
  const [pendingOrders, setPendingOrders] = useState<QrApprovedOrder[]>([]);

  // UI State
  const [Qrcodedont, setQrcodedont] = useState(false);
  const [QrcodedontOrder, setQrcodedontOrder] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  // cartNotification eski sistemdi (in-card pop-up); artık Dynamic Island üzerinden
  // tek bir noktadan gösteriliyor (island.show({type:'cart',...})).
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem('rezmo-theme');
    return (saved as any) === 'dark' ? 'dark' : 'light';
  });

  // Payment
  const [paymentAvailability, setPaymentAvailability] = useState<any>(null);
  const [googlePayConfig, setGooglePayConfig] = useState<any>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [applePayAvailable, setApplePayAvailable] = useState(false);

  // Auth user (Google / Apple / Phone) - loaded from localStorage
  const [user, setUser] = useState<QrUserProfile | null>(() => loadStoredUser());

  // Auto-poll table data every 15 seconds while on this page so the orders tab stays fresh
  // (only after at least one fetch has succeeded to avoid spamming on errors)
  const [tablePolling, setTablePolling] = useState(false);

  // Order tracking state — populated from /api/qr/{token}/orders/active and the
  // single tracking endpoint for any locally-remembered orders that are no
  // longer in the active feed (so we can show canceled/completed states).
  const [trackingOrders, setTrackingOrders] = useState<QrTrackingOrder[]>([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingLastUpdatedAt, setTrackingLastUpdatedAt] = useState<number | undefined>(undefined);

  // ---------- QR Adisyon (live bill) ----------
  // Per docs/QR_ADISYON_FRONTEND.md, the bill tab now reads from a richer
  // endpoint that includes time charges, discounts, prepayments, partial
  // payments and proper totals. The phone-based customer identify endpoint
  // unlocks loyalty points and order history. WebSocket pushes (with poll
  // fallback) keep this view live so the customer sees waiter actions as
  // they happen.
  const [adisyon, setAdisyon] = useState<QrAdisyonResponse | null>(null);
  const [adisyonLoading, setAdisyonLoading] = useState(false);
  const [adisyonError, setAdisyonError] = useState<string | null>(null);
  const [adisyonLastUpdatedAt, setAdisyonLastUpdatedAt] = useState<number | undefined>(undefined);

  const [qrCustomer, setQrCustomer] = useState<QrAdisyonCustomer | null>(null);
  const [qrLifetime, setQrLifetime] = useState<QrCustomerLifetime | null>(null);
  const [identifyLoading, setIdentifyLoading] = useState(false);
  const [identifyError, setIdentifyError] = useState<string | null>(null);
  const [identifyNotFound, setIdentifyNotFound] = useState(false);

  const [qrHistory, setQrHistory] = useState<QrHistoryResponse | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // ---------- QR Waiter call ----------
  // Per docs/QR_WAITER_CALL.md the customer can summon the waiter with a
  // typed reason code. The backend dedupes calls in a 60s window and
  // broadcasts status changes (`pending` → `acknowledged` → `resolved`)
  // through the public `restaurant.{rid}.waiter-calls` channel. We mirror
  // the same WebSocket-with-poll-fallback pattern as the adisyon hook so
  // the customer's UI never goes stale.
  const [activeWaiterCall, setActiveWaiterCall] = useState<QrWaiterActiveCall | null>(null);
  const [waiterCallLoading, setWaiterCallLoading] = useState(false);
  const [waiterCallError, setWaiterCallError] = useState<string | null>(null);

  // Redux basket
  const basketItems = useSelector((state: any) => state.basket.items || []);

  // Apply theme to root
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('rezmo-theme', theme);
  }, [theme]);

  // ============ QR Adisyon: live bill ============
  // Snapshot fetcher — includes the customer phone (when known) so the
  // backend bundles loyalty info into the response.
  const fetchAdisyon = useCallback(
    async (signal?: AbortSignal) => {
      if (!token || Qrcodedont) return;
      try {
        setAdisyonLoading(true);
        const data = await getQrAdisyon(token, qrCustomer?.phone || null, signal);
        setAdisyon(data);
        setAdisyonError(null);
        setAdisyonLastUpdatedAt(Date.now());
        // The /adisyon endpoint may attach a fresher customer block if the
        // phone is known — keep the local profile in sync without a second
        // round trip to /customer/identify.
        if (data.customer) {
          setQrCustomer(data.customer);
        }
      } catch (err: any) {
        if (axios.isCancel(err) || err?.name === 'CanceledError') return;
        console.error('Adisyon fetch failed', err);
        setAdisyonError(err?.response?.data?.message || err?.message || 'Failed to load bill');
      } finally {
        setAdisyonLoading(false);
      }
    },
    [token, Qrcodedont, qrCustomer?.phone],
  );

  // Initial fetch + refetch whenever the token, restaurant or customer phone changes.
  useEffect(() => {
    if (!token || Qrcodedont) return;
    const ac = new AbortController();
    fetchAdisyon(ac.signal);
    return () => ac.abort();
  }, [fetchAdisyon, token, Qrcodedont]);

  // Real-time updates via Reverb/Pusher with polling fallback. The hook calls
  // fetchAdisyon on every relevant event (new order, payment created, status
  // changes, etc.) and also every 10s when the websocket is unavailable.
  useQrAdisyonRealtime({
    uniqueUrl: token || null,
    restaurantId: adisyon?.restaurant?.id ?? null,
    tableId: adisyon?.table?.id ?? null,
    onRefresh: () => {
      fetchAdisyon();
    },
    pollIntervalMs: 10000,
    disabled: Qrcodedont,
  });

  // ============ QR Adisyon: customer identify ============
  const handleQrIdentifyCustomer = useCallback(
    async (rawPhone: string) => {
      if (!token) return;
      const phone = rawPhone.trim();
      if (!phone) {
        setIdentifyError(getQrTranslations().phoneInvalidFormat);
        return;
      }
      try {
        setIdentifyLoading(true);
        setIdentifyError(null);
        setIdentifyNotFound(false);
        const res = await identifyQrCustomer(token, phone);
        if (res.found && res.customer) {
          setQrCustomer(res.customer);
          setQrLifetime(res.lifetime);
          saveQrPhone(token, phone);
        } else {
          setIdentifyNotFound(true);
          setQrCustomer(null);
          setQrLifetime(res.lifetime);
        }
      } catch (err: any) {
        if (err?.response?.status === 422) {
          setIdentifyError(getQrTranslations().phoneInvalidFormat);
        } else {
          setIdentifyError(
            err?.response?.data?.message ||
              err?.message ||
              getQrTranslations().phoneInvalidFormat,
          );
        }
      } finally {
        setIdentifyLoading(false);
      }
    },
    [token],
  );

  const handleQrForgetCustomer = useCallback(() => {
    if (token) clearQrPhone(token);
    setQrCustomer(null);
    setQrLifetime(null);
    setQrHistory(null);
    setIdentifyNotFound(false);
    setIdentifyError(null);
  }, [token]);

  // Auto-restore: if the customer typed their phone before, re-identify on
  // mount so the loyalty section + history stay populated across refreshes.
  useEffect(() => {
    if (!token || Qrcodedont) return;
    const stored = loadStoredQrPhone(token);
    if (!stored) return;
    handleQrIdentifyCustomer(stored);
    // intentionally empty deps: only run when token first becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, Qrcodedont]);

  // ============ QR Adisyon: history ============
  const handleQrLoadHistory = useCallback(
    async (page = 1, perPage = 10) => {
      if (!token || !qrCustomer?.phone) return;
      try {
        setHistoryLoading(true);
        setHistoryError(null);
        const res = await getQrCustomerHistory(token, qrCustomer.phone, page, perPage);
        setQrHistory(res);
      } catch (err: any) {
        console.error('History fetch failed', err);
        setHistoryError(err?.response?.data?.message || err?.message || 'Failed to load history');
      } finally {
        setHistoryLoading(false);
      }
    },
    [token, qrCustomer?.phone],
  );

  // ============ QR Waiter call ============
  // Pull the currently-active call for this QR table (used on mount + on
  // websocket reconnect). 404/empty responses are normal when no call is
  // active — the API returns `{ call: null }` not an error.
  const fetchActiveWaiterCall = useCallback(async () => {
    if (!token || Qrcodedont) return;
    try {
      const res = await getQrActiveWaiterCall(token);
      setActiveWaiterCall(res.call);
    } catch (err: any) {
      // Silent on transient errors — the realtime hook will re-attempt
      // on every reconnect, so a one-off failure isn't user-facing.
      if (axios.isCancel(err) || err?.name === 'CanceledError') return;
      // Only log to help with debugging; don't surface to UI.
      console.warn('Active waiter call fetch failed', err?.message || err);
    }
  }, [token, Qrcodedont]);

  // POST /call-waiter — the response body already contains the new call so
  // we use it directly to set local state (no extra round-trip needed).
  const handleSubmitWaiterCall = useCallback(
    async (reasonCode: QrWaiterReasonCode, note?: string | null) => {
      if (!token) return;
      try {
        setWaiterCallLoading(true);
        setWaiterCallError(null);
        const res = await createQrWaiterCall(token, { reason_code: reasonCode, note });
        // Map the create-response onto the active-call shape so the same
        // UI block renders both newly-created and already-pending calls.
        setActiveWaiterCall({
          call_id: res.call_id,
          status: res.status,
          reason_code: res.reason_code,
          reason_label: res.reason_label,
          note: res.note,
          assigned_waiter: res.assigned_waiter,
          created_at: res.created_at,
          acknowledged_at: null,
        });
        if (res.already_pending) {
          island.show({
            type: 'info',
            title: getQrTranslations().waiterCallAlreadyPending,
          });
        } else {
          island.show({
            type: 'waiter',
            title: getQrTranslations().waiterCallToastSent,
          });
        }
      } catch (err: any) {
        // Throttle (429) gets a dedicated friendly message; everything
        // else falls back to the generic error string.
        const tr = getQrTranslations();
        if (err?.response?.status === 429) {
          setWaiterCallError(tr.waiterCallErrorThrottled);
        } else if (err?.code === 'ERR_NETWORK' || !err?.response) {
          setWaiterCallError(tr.waiterCallErrorNetwork);
        } else {
          setWaiterCallError(
            err?.response?.data?.message || err?.message || tr.waiterCallErrorGeneric,
          );
        }
      } finally {
        setWaiterCallLoading(false);
      }
    },
    [token],
  );

  // DELETE /call-waiter/{id} — used by the "Tapped by mistake" button while
  // status is still pending. After the request the WebSocket will broadcast
  // a `resolved` status-changed event and the realtime hook will clear our
  // state, but we also clear locally so the UI feels instant.
  const handleCancelWaiterCall = useCallback(async () => {
    if (!token || !activeWaiterCall) return;
    try {
      setWaiterCallLoading(true);
      setWaiterCallError(null);
      await cancelQrWaiterCall(token, activeWaiterCall.call_id);
      setActiveWaiterCall(null);
      island.show({
        type: 'success',
        title: getQrTranslations().waiterCallToastCancelled,
      });
    } catch (err: any) {
      const tr = getQrTranslations();
      setWaiterCallError(
        err?.response?.data?.message || err?.message || tr.waiterCallErrorGeneric,
      );
    } finally {
      setWaiterCallLoading(false);
    }
  }, [token, activeWaiterCall]);

  // Initial fetch — restore an active call on page reload so the customer
  // doesn't lose their pending request after closing the tab.
  useEffect(() => {
    if (!token || Qrcodedont) return;
    fetchActiveWaiterCall();
  }, [fetchActiveWaiterCall, token, Qrcodedont]);

  // Realtime status updates for the customer's own call. The hook filters
  // events by call_id so we never react to other tables' calls leaking
  // through the same restaurant channel.
  useQrWaiterCallRealtime({
    uniqueUrl: token || null,
    restaurantId: adisyon?.restaurant?.id ?? null,
    activeCallId: activeWaiterCall?.call_id ?? null,
    onStatusChanged: (event) => {
      const tr = getQrTranslations();
      if (event.status === 'resolved') {
        // Waiter completed (or customer cancelled and the broadcast came
        // back to us). Clear the card and show a small confirmation.
        setActiveWaiterCall(null);
        island.show({
          type: 'success',
          title: tr.waiterCallToastResolved,
        });
      } else if (event.status === 'acknowledged') {
        // Promote pending → acknowledged. Use the broadcast's
        // acknowledged_by_name to fill in the assigned waiter when it
        // wasn't known yet (e.g. waiter only assigned post-call).
        setActiveWaiterCall((prev) => {
          if (!prev) return prev;
          const ack = event.acknowledged_by_name && event.acknowledged_by
            ? { id: event.acknowledged_by, name: event.acknowledged_by_name }
            : prev.assigned_waiter;
          return {
            ...prev,
            status: 'acknowledged',
            acknowledged_at: event.occurred_at,
            assigned_waiter: ack,
          };
        });
      } else {
        // Some other status — fall back to refetching the canonical state.
        fetchActiveWaiterCall();
      }
    },
    onRefresh: fetchActiveWaiterCall,
    pollIntervalMs: 8000,
    disabled: Qrcodedont,
  });

  // Apple Pay availability
  useEffect(() => {
    setApplePayAvailable(isApplePayAvailable());
  }, []);

  // ============ Data Fetching ============
  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const response = await axios.get(`${base_url}/qr/${token}/menu`, getAuthHeaders());
        setMenu(response.data.stockGroups || response.data.data?.stockGroups || []);

        const resData = response.data.restaurant || response.data.data?.restaurant;
        const msg = response.data.message || response.data.data?.message;
        const masaName =
          response.data.table?.name ||
          response.data.data?.table?.name ||
          response.data.table_name ||
          msg ||
          '';

        if (resData) {
          setRestaurant((prev) => ({
            ...prev,
            name: resData.name,
            logo: resData.logo ? `${img_url}/${resData.logo}` : '',
            image: resData.image ? `${img_url}/${resData.image}` : undefined,
            masaName: masaName || prev.masaName,
          }));
        }
      } catch (error: any) {
        if (error.response && error.response.status === 400) {
          setQrcodedont(true);
          const resData = error.response.data.restaurant || error.response.data.data?.restaurant;
          if (resData) {
            setRestaurant({
              name: resData.name,
              logo: resData.logo ? `${img_url}/${resData.logo}` : '',
              masaName: '',
            });
          }
        } else {
          console.error('Error fetching menu data:', error);
        }
      }
    };

    const fetchTableData = async () => {
      try {
        const response = await axios.get(`${base_url}/qr/${token}/table`, getAuthHeaders());
        const tableData = response.data.table || response.data.data?.table || response.data;
        const msg = response.data.message || response.data.data?.message;
        const tableName =
          tableData?.name ||
          tableData?.masaName ||
          tableData?.table_name ||
          response.data?.name ||
          response.data?.tableName ||
          response.data?.table_name ||
          msg ||
          '';

        if (tableName) {
          setRestaurant((prev) => ({ ...prev, masaName: tableName }));
        }

        if (tableData?.orders) {
          setApprovedOrders(tableData.orders.approved?.orders || []);
          setPendingOrders(tableData.orders.pending_approval?.orders || []);
        }
      } catch (error: any) {
        console.error('Error fetching table data:', error);
      }
    };

    const fetchStockSets = async () => {
      try {
        const response = await axios.get(`${base_url}/stock-sets`, getAuthHeaders());
        setStockSets(response.data || []);
      } catch (e) {
        // Optional - fail silently
      }
    };

    if (!Qrcodedont) {
      fetchTableData();
      setTablePolling(true);
    }
    fetchMenuData();
    fetchStockSets();
  }, [token, Qrcodedont]);

  // Poll for fresh table/order data so the order history is always up to date
  useEffect(() => {
    if (!token || !tablePolling || Qrcodedont) return;
    const id = setInterval(async () => {
      try {
        const response = await axios.get(`${base_url}/qr/${token}/table`, getAuthHeaders());
        const td = response.data.table || response.data.data?.table || response.data;
        if (td?.orders) {
          setApprovedOrders(td.orders.approved?.orders || []);
          setPendingOrders(td.orders.pending_approval?.orders || []);
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 15000);
    return () => clearInterval(id);
  }, [token, tablePolling, Qrcodedont]);

  // ============ Order Tracking (live polling) ============
  // Per QR_ORDER_TRACKING.md: poll /api/qr/{token}/orders/active every 7s.
  // For any locally-remembered order ID that is no longer in the active feed,
  // hit the single /tracking endpoint to learn whether it was completed or
  // canceled (so we can briefly show that final state to the user).
  useEffect(() => {
    if (!token || Qrcodedont) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchSingleTracking = async (
      orderId: number,
    ): Promise<QrTrackingOrder | null> => {
      try {
        const res = await axios.get(
          `${base_url}/qr/${token}/orders/${orderId}/tracking`,
          getAuthHeaders(),
        );
        const payload = (res.data?.data || res.data) as QrTrackingOrder;
        return payload && payload.order_id ? payload : null;
      } catch {
        return null;
      }
    };

    const poll = async () => {
      if (cancelled) return;
      setTrackingLoading(true);
      try {
        const res = await axios.get(
          `${base_url}/qr/${token}/orders/active`,
          getAuthHeaders(),
        );
        if (cancelled) return;
        const activeList: QrTrackingOrder[] =
          (res.data?.orders as QrTrackingOrder[]) ||
          (res.data?.data?.orders as QrTrackingOrder[]) ||
          [];

        // Remember any IDs returned by the server (covers refresh case where
        // localStorage was cleared but the table actually has open orders).
        activeList.forEach((o) => rememberOrderId(token, o.order_id));

        // For any locally-remembered ID that is NOT in the active list, fetch
        // its single tracking record to see if it completed or got canceled.
        // Skip those that are already older than ~30 minutes from our last
        // sighting (the user has likely moved on).
        const activeIds = new Set(activeList.map((o) => o.order_id));
        const remembered = getRememberedOrderIds(token);
        const missingIds = remembered.filter((id) => !activeIds.has(id));

        const finalised: QrTrackingOrder[] = [];
        if (missingIds.length > 0) {
          // Limit how many we look up per tick to avoid hammering the API
          // (the spec allows 60/min so 3 lookups per 7s poll is well under).
          const subset = missingIds.slice(-3);
          const results = await Promise.all(subset.map(fetchSingleTracking));
          results.forEach((r) => {
            if (!r) return;
            // Only show recently-finalised orders (canceled/completed).
            // For completed orders, we keep them in the list briefly so the
            // user sees the "Served ✓" state right after the waiter delivers.
            if (r.is_canceled || r.status === 'completed' || r.status === 'canceled') {
              finalised.push(r);
            }
          });
        }

        if (cancelled) return;
        setTrackingOrders([...activeList, ...finalised]);
        setTrackingLastUpdatedAt(Date.now());

        // If everything is final (canceled/completed) we slow down polling.
        const stillLive = activeList.length > 0;
        timer = setTimeout(poll, stillLive ? 7000 : 20000);
      } catch (err: any) {
        if (cancelled) return;
        // 404/403 etc. — backoff to 15s on error per spec
        timer = setTimeout(poll, 15000);
      } finally {
        if (!cancelled) setTrackingLoading(false);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, Qrcodedont]);

  // ============ Payment Settings ============
  useEffect(() => {
    if (!token) return;
    const fetchPaymentSettings = async () => {
      try {
        const [availRes, gpayRes] = await Promise.all([
          axios.get(`${base_url}/qr/${token}/payment/check`).catch(() => null),
          axios.get(`${base_url}/qr/${token}/payment/google-pay/config`).catch(() => null),
        ]);
        if (availRes?.data) setPaymentAvailability(availRes.data);
        if (gpayRes?.data) setGooglePayConfig(gpayRes.data);
      } catch (err: any) {
        console.error('Payment API errors', err);
      }
    };
    fetchPaymentSettings();
  }, [token]);

  // ============ Data Mapping (API → V2 props) ============

  const categories: QrCategory[] = useMemo(() => {
    const categoryEmojis: Record<string, string> = {
      burger: '🍔',
      pizza: '🍕',
      makarna: '🍝',
      salata: '🥗',
      et: '🥩',
      sushi: '🍣',
      içecek: '🍹',
      tatlı: '🍰',
      kahvaltı: '🍳',
      çorba: '🥣',
      kebap: '🥙',
      deniz: '🐟',
      tavuk: '🍗',
      vegan: '🥬',
      kahve: '☕',
      set: '🎁',
    };
    const getEmoji = (name?: string) => {
      const lower = (name || '').toLowerCase();
      for (const [key, emoji] of Object.entries(categoryEmojis)) {
        if (lower.includes(key)) return emoji;
      }
      return '🍽️';
    };
    const cats: QrCategory[] = [{ id: 'all', name: 'Tümü', icon: '🍽️' }];
    menu.forEach((cat) =>
      cats.push({
        id: cat.id,
        name: cat.name,
        icon: getEmoji(cat.name),
      }),
    );
    if (stockSets.length > 0) {
      cats.push({ id: 'sets', name: 'Setler', icon: '🎁' });
    }
    return cats;
  }, [menu, stockSets]);

  const products: QrProduct[] = useMemo(() => {
    const all: QrProduct[] = [];
    menu.forEach((cat) => {
      (cat.stocks || []).forEach((stock: any, idx: number) => {
        const price = parseFloat(stock.price) || 0;
        all.push({
          id: stock.id,
          name: stock.name,
          description: stock.description || '',
          price,
          image: stock.image,
          category: cat.id,
          categoryName: cat.name,
          rating: stock.rating || 4.5 + (idx % 5) * 0.1,
          isFeatured: !!stock.isFeatured || idx % 7 === 0,
          isNew: !!stock.isNew || idx % 11 === 0,
          isPopular: !!stock.isPopular,
          details: stock.details,
          stocks: stock.stocks,
        });
      });
    });
    if (selectedCategory === 'sets' && stockSets.length > 0) {
      stockSets.forEach((set: any) => {
        const price = parseFloat(set.price) || 0;
        all.push({
          id: set.id,
          name: set.name,
          description: set.description || 'Set Menü',
          price,
          image: set.image,
          category: 'sets',
          categoryName: 'Setler',
          rating: 4.7,
          isFeatured: true,
          stocks: set.stocks,
        });
      });
    }
    return all;
  }, [menu, stockSets, selectedCategory]);

  const stories: QrStory[] = useMemo(() => {
    return menu.slice(0, 6).map((cat) => {
      const firstStock = cat.stocks?.[0];
      const items = (cat.stocks || []).slice(0, 3).map((stock: any) => ({
        image: stock.image || firstStock?.image,
        title: stock.name,
        description:
          stock.description || `${parseFloat(stock.price || 0).toFixed(0)} ₼`,
      }));
      return {
        id: cat.id,
        title: cat.name,
        image: firstStock?.image,
        items: items.length > 0 ? items : [{ title: cat.name, image: firstStock?.image }],
      };
    });
  }, [menu]);

  const cartItems: QrCartItem[] = useMemo(
    () =>
      basketItems.map((b: any) => ({
        id: b.id,
        detail_id: b.detail_id || null,
        name: b.name,
        price: b.price,
        quantity: b.quantity,
        image: b.image,
      })),
    [basketItems],
  );

  // ============ Handlers ============

  const handleAddToCart = useCallback(
    (product: QrProduct, quantity = 1, detailId: string | number | null = null) => {
      const detail = detailId
        ? (product.details || []).find((d: any) => d.id === detailId)
        : null;
      const finalPrice = detail ? Number(detail.price) : Number(product.price);
      dispatch(
        addItem({
          id: product.id,
          name: product.name,
          price: finalPrice,
          quantity,
          image: product.image || null,
          detail_id: detailId || null,
          desc: detail ? `${detail.count} ${detail.unit}` : null,
          details: product.details,
          originalPrice: Number(product.price),
        }),
      );
      island.show({
        type: 'cart',
        title: getQrTranslations().notifAddedToCart,
        stockName: product.name,
        quantity,
        image: product.image
          ? product.image.startsWith('http')
            ? product.image
            : `${img_url}/${product.image}`
          : null,
      });
    },
    [dispatch],
  );

  const handleUpdateQuantity = useCallback(
    (id: string | number, quantity: number, detailId: string | number | null = null) => {
      if (quantity <= 0) {
        dispatch(removeItem({ id, detail_id: detailId || null }));
        return;
      }
      const item = basketItems.find(
        (i: any) => i.id === id && (i.detail_id || null) === (detailId || null),
      );
      if (!item) return;
      if (quantity > item.quantity) {
        dispatch(increaseQuantity({ id, detail_id: detailId || null }));
      } else {
        dispatch(decreaseQuantity({ id, detail_id: detailId || null }));
      }
    },
    [dispatch, basketItems],
  );

  const handleRemoveItem = useCallback(
    (id: string | number, detailId: string | number | null = null) => {
      dispatch(removeItem({ id, detail_id: detailId || null }));
    },
    [dispatch],
  );

  const handleCheckout = useCallback(async () => {
    try {
      const stocks = basketItems
        .filter((item: any) => !stockSets.some((set: any) => set.id === item.id))
        .map((item: any) => ({
          stock_id: item.id,
          quantity: item.quantity,
          detail_id: item.detail_id || null,
        }));

      const stock_sets = basketItems
        .filter((item: any) => stockSets.some((set: any) => set.id === item.id))
        .map((item: any) => ({
          stock_set_id: item.id,
          quantity: item.quantity,
          detail_id: item.detail_id || null,
        }));

      const orderData: any = {};
      if (stocks.length > 0) orderData.stocks = stocks;
      if (stock_sets.length > 0) orderData.stock_sets = stock_sets;

      if (!orderData.stocks && !orderData.stock_sets) {
        island.show({
          type: 'warn',
          title: getQrTranslations().cartEmptyToast,
        });
        return;
      }

      const orderResp = await axios.post(
        `${base_url}/qr/${token}/order`,
        orderData,
        getAuthHeaders(),
      );
      dispatch(clearBasket());
      island.show({
        type: 'success',
        title: getQrTranslations().orderSent,
      });

      // Persist the order ID under `qr_order_ids:{token}` so the customer can
      // recover the tracking view after a refresh and so we can detect when
      // the order leaves the /orders/active feed (canceled/completed).
      const newOrderId =
        orderResp.data?.id ||
        orderResp.data?.order_id ||
        orderResp.data?.data?.id ||
        orderResp.data?.data?.order_id;
      if (newOrderId && token) {
        rememberOrderId(token, newOrderId);
      }

      // Optimistic add of pending order so tracking/history tabs immediately show data
      const optimistic: QrApprovedOrder = {
        order_id: newOrderId || `tmp-${Date.now()}`,
        total_price:
          orderResp.data?.total_price ||
          basketItems.reduce(
            (sum: number, b: any) => sum + parseFloat(String(b.price || 0)) * (b.quantity || 1),
            0,
          ),
        stocks: basketItems.map((b: any) => ({
          name: b.name,
          quantity: b.quantity,
          price: b.price,
        })),
      };
      setPendingOrders((prev) => [...prev, optimistic]);

      // Trigger an immediate tracking refresh so the customer sees the new
      // order in the tracking tab without having to wait for the next poll.
      if (newOrderId && token) {
        try {
          const trackingRes = await axios.get(
            `${base_url}/qr/${token}/orders/${newOrderId}/tracking`,
            getAuthHeaders(),
          );
          const tracked = (trackingRes.data?.data || trackingRes.data) as QrTrackingOrder;
          if (tracked && tracked.order_id) {
            setTrackingOrders((prev) => {
              const without = prev.filter((o) => o.order_id !== tracked.order_id);
              return [...without, tracked];
            });
            setTrackingLastUpdatedAt(Date.now());
          }
        } catch {
          /* polling effect will pick it up shortly */
        }
      }

      // Refresh table info from server (overrides optimistic with real data)
      try {
        const refresh = await axios.get(`${base_url}/qr/${token}/table`, getAuthHeaders());
        const td = refresh.data.table || refresh.data.data?.table || refresh.data;
        if (td?.orders) {
          setApprovedOrders(td.orders.approved?.orders || []);
          setPendingOrders(td.orders.pending_approval?.orders || []);
        }
      } catch (e) {
        // ignore - optimistic order is still visible
      }
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        setQrcodedontOrder(true);
      } else {
        console.error('Error submitting order:', error);
        island.show({
          type: 'error',
          title: getQrTranslations().orderFailed,
        });
      }
    }
  }, [basketItems, stockSets, token, dispatch]);

  // NOTE: The old `handleCallWaiter` placeholder was removed in favour of
  // the typed waiter-call API (see handleSubmitWaiterCall above). The new
  // WAITER tab handles its own toasts so the cart-notification overlay is
  // no longer needed for this flow.

  const handlePayOnline = useCallback(async () => {
    try {
      setIsProcessingPayment(true);
      const payload = {
        amount: paymentAvailability?.amount || 0,
        order_id: 0,
      };
      const response = await axios.post(`${base_url}/qr/${token}/payment/create`, payload);
      if (response.data?.data?.redirect_url) {
        window.location.href = response.data.data.redirect_url;
      } else {
        island.show({
          type: 'error',
          title: getQrTranslations().paymentLinkFailed,
        });
      }
    } catch (err: any) {
      island.show({
        type: 'error',
        title: err.response?.data?.message || getQrTranslations().paymentError,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [token, paymentAvailability]);

  const handlePayGooglePay = useCallback(
    async (paymentData: any) => {
      try {
        setIsProcessingPayment(true);
        const tokenJson = JSON.parse(paymentData.paymentMethodData.tokenizationData.token);
        const payload = {
          google_pay_token: JSON.stringify(tokenJson),
          amount: paymentAvailability?.amount || 0,
          order_id: 0,
        };
        const response = await axios.post(
          `${base_url}/qr/${token}/payment/google-pay/process`,
          payload,
        );
        if (response.data?.paid) {
          island.show({
            type: 'success',
            title: getQrTranslations().paymentSuccess,
          });
          // refresh
          try {
            const refresh = await axios.get(`${base_url}/qr/${token}/table`, getAuthHeaders());
            const td = refresh.data.table || refresh.data.data?.table || refresh.data;
            if (td?.orders) {
              setApprovedOrders(td.orders.approved?.orders || []);
              setPendingOrders(td.orders.pending_approval?.orders || []);
            }
          } catch (e) { }
        } else {
          island.show({
            type: 'error',
            title: `${getQrTranslations().paymentError}: ${response.data?.message || ''}`,
          });
        }
      } catch (err: any) {
        island.show({
          type: 'error',
          title: err.response?.data?.message || getQrTranslations().googlePayError,
        });
      } finally {
        setIsProcessingPayment(false);
      }
    },
    [token, paymentAvailability],
  );

  const handlePayApplePay = useCallback(async () => {
    if (!applePayAvailable) {
      island.show({
        type: 'warn',
        title: getQrTranslations().applePayUnavailableMsg,
      });
      return;
    }
    try {
      setIsProcessingPayment(true);
      const amount = paymentAvailability?.amount || 0;
      // @ts-ignore
      const session = new window.ApplePaySession(3, {
        countryCode: 'TR',
        currencyCode: 'TRY',
        merchantCapabilities: ['supports3DS'],
        supportedNetworks: ['visa', 'masterCard', 'amex'],
        total: { label: restaurant.name || 'Rezmo', amount: amount.toFixed(2) },
      });

      session.onvalidatemerchant = async (event: any) => {
        try {
          const validateRes = await axios.post(
            `${base_url}/qr/${token}/payment/apple-pay/validate`,
            { validation_url: event.validationURL },
          );
          if (validateRes.data?.merchantSession) {
            session.completeMerchantValidation(validateRes.data.merchantSession);
          } else {
            session.abort();
            island.show({
              type: 'error',
              title: getQrTranslations().applePayValidationFailed,
            });
          }
        } catch (e: any) {
          session.abort();
          island.show({
            type: 'error',
            title: getQrTranslations().applePayNotConfigured,
          });
        }
      };

      session.onpaymentauthorized = async (event: any) => {
        try {
          const payload = {
            apple_pay_token: JSON.stringify(event.payment.token),
            amount,
            order_id: 0,
          };
          const response = await axios.post(
            `${base_url}/qr/${token}/payment/apple-pay/process`,
            payload,
          );
          if (response.data?.paid) {
            // @ts-ignore
            session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
            island.show({
              type: 'success',
              title: getQrTranslations().paymentSuccess,
            });
          } else {
            // @ts-ignore
            session.completePayment(window.ApplePaySession.STATUS_FAILURE);
            island.show({
              type: 'error',
              title: getQrTranslations().applePayCannotComplete,
            });
          }
        } catch (err: any) {
          // @ts-ignore
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          island.show({
            type: 'error',
            title: getQrTranslations().applePayProcessFailed,
          });
        }
      };

      session.begin();
    } catch (err: any) {
      island.show({
        type: 'error',
        title: getQrTranslations().applePayStartFailed,
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [applePayAvailable, paymentAvailability, restaurant.name, token, island]);

  // ============ Renders ============

  if (QrcodedontOrder)
    return <QrcodeDontOrder onClose={() => setQrcodedontOrder(false)} />;
  if (Qrcodedont) return <QrcodeDontActive onClose={() => setQrcodedont(false)} />;

  return (
    <>
      <Helmet>
        <title>{restaurant.name ? `${restaurant.name} • QR Menu` : 'QR Menu'}</title>
        <meta
          name="description"
          content="Restoran QR menü - Hızlı sipariş, kolay ödeme"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
      </Helmet>

      {showWelcome ? (
        <QRMenuWelcome
            masaName={restaurant.masaName}
            restaurantName={restaurant.name}
            logo={restaurant.logo}
            onStart={() => setShowWelcome(false)}
          />
        ) : (
          <QRMenuPage
            restaurantName={restaurant.name}
            restaurantLogo={restaurant.logo}
            restaurantImage={restaurant.image}
            masaName={restaurant.masaName}
            categories={categories}
            products={products}
            stories={stories}
            cartItems={cartItems}
            approvedOrders={approvedOrders}
            pendingOrders={pendingOrders}
            trackingOrders={trackingOrders}
            trackingLoading={trackingLoading}
            trackingLastUpdatedAt={trackingLastUpdatedAt}
            adisyon={adisyon}
            adisyonLoading={adisyonLoading}
            adisyonError={adisyonError}
            adisyonLastUpdatedAt={adisyonLastUpdatedAt}
            qrCustomer={qrCustomer}
            qrLifetime={qrLifetime}
            identifyLoading={identifyLoading}
            identifyError={identifyError}
            identifyNotFound={identifyNotFound}
            qrHistory={qrHistory}
            historyLoading={historyLoading}
            historyError={historyError}
            onIdentifyCustomer={handleQrIdentifyCustomer}
            onForgetCustomer={handleQrForgetCustomer}
            onLoadHistory={handleQrLoadHistory}
            activeWaiterCall={activeWaiterCall}
            waiterCallLoading={waiterCallLoading}
            waiterCallError={waiterCallError}
            onSubmitWaiterCall={handleSubmitWaiterCall}
            onCancelWaiterCall={handleCancelWaiterCall}
            selectedCategory={selectedCategory}
            searchQuery={searchQuery}
            currency="₼"
            theme={theme}
            onToggleTheme={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            paymentEnabled={!!paymentAvailability?.enabled}
            paymentAmount={paymentAvailability?.amount || 0}
            googlePayConfig={googlePayConfig}
            isProcessingPayment={isProcessingPayment}
            applePayAvailable={applePayAvailable}
            user={user}
            onUserChange={setUser}
            googleClientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}
            appleServiceId={process.env.REACT_APP_APPLE_SERVICE_ID}
            onSearch={setSearchQuery}
            onCategoryChange={setSelectedCategory}
            onAddToCart={handleAddToCart}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onCheckout={handleCheckout}
            onBackToWelcome={() => setShowWelcome(true)}
            onPayOnline={handlePayOnline}
            onPayGooglePay={handlePayGooglePay}
            onPayApplePay={handlePayApplePay}
            onFeedback={async (payload: FeedbackPayload) => {
              // Send feedback to server (if endpoint exists). Silently noop on 404.
              try {
                await axios.post(
                  `${base_url}/qr/${token}/feedback`,
                  {
                    rating: payload.rating,
                    category: payload.category,
                    questions: payload.questions,
                    message: payload.message,
                    user: user
                      ? { name: user.name, email: user.email, provider: user.provider }
                      : undefined,
                  },
                  getAuthHeaders(),
                );
              } catch (e) {
                // Ignore - feedback already shown to user; backend endpoint may not exist yet.
              }
            }}
        />
      )}
    </>
  );
};

/**
 * Dış sarmalayıcı — tüm provider'ları mount eder. Sıralama:
 *   QrLanguage  → metinler için (en dışta ki ProfileModal vs. de erişsin)
 *   CustomerAuth → customer Sanctum token + profil state
 *   DynamicIsland → tüm bildirimler için tek host (toast yerine)
 *   CustomerFavorites → auth'a bağımlı — içeride mount et
 */
const RestaurantMenuV2: React.FC = () => (
  <QrLanguageProvider>
    <CustomerAuthProvider>
      <DynamicIslandProvider>
        <CustomerFavoritesProvider>
          <RestaurantMenuV2Body />
        </CustomerFavoritesProvider>
      </DynamicIslandProvider>
    </CustomerAuthProvider>
  </QrLanguageProvider>
);

export default RestaurantMenuV2;
